<template>
  <li class="profile-nav onhover-dropdown pe-0 py-0">
    <div
      class="media profile-media position-relative d-flex align-items-center profile-trigger-hover-bridge"
    >
      <div class="profile-icon profile-icon--always-visible flex-shrink-0">
        <span
          class="rounded-circle d-flex align-items-center justify-content-center fw-semibold profile-avatar-sm"
          :class="invertColor ? 'bg-white text-primary' : 'bg-primary text-white'"
          >{{ initials }}</span
        >
      </div>
      <div class="media-body ms-0" :class="{ 'text-inverted': invertColor }">
        <span>{{ firstName }}</span>
        <p class="mb-0 font-roboto">{{ lastName }} <i class="middle fa fa-angle-down"></i></p>
      </div>
    </div>
    <ul class="profile-dropdown onhover-show-div">
      <!-- User info header: email always visible, name/surname only in circle mode -->
      <li class="py-2 px-3 border-bottom profile-dropdown-header">
        <div class="d-flex flex-column min-w-0">
          <span
            class="fw-semibold text-truncate profile-dropdown-header__name"
            :title="`${firstName} ${lastName}`.trim()"
            >{{ firstName }} {{ lastName }}</span
          >
          <span
            v-if="email"
            class="small text-muted text-truncate profile-dropdown-header__email"
            :title="email"
            >{{ email }}</span
          >
        </div>
      </li>
      <li v-if="canSwitchUserGroups">
        <a @click="openUserGroupSwitcher">
          <vue-feather type="shuffle"></vue-feather>
          <span style="text-transform: none">Switch Company Entity</span>
          <span v-if="availableUserGroups" class="badge bg-primary ms-2">{{
            availableUserGroups.length
          }}</span>
        </a>
      </li>
      <li>
        <a @click="refreshPermissions">
          <vue-feather type="refresh-cw"></vue-feather>
          <span style="text-transform: none">{{
            refreshingPermissions ? 'Refreshing...' : 'Refresh Permissions'
          }}</span>
        </a>
      </li>

      <li v-if="isApiPartner">
        <router-link :to="'/account-management/account-settings'"
          ><vue-feather type="settings"></vue-feather
          ><span style="text-transform: none">Account Settings</span></router-link
        >
      </li>
      <li>
        <router-link :to="'/users/profile'"
          ><vue-feather type="user"></vue-feather
          ><span style="text-transform: none">User Profile</span></router-link
        >
      </li>
      <li>
        <router-link :to="'/profile/security'"
          ><vue-feather type="shield"></vue-feather
          ><span style="text-transform: none">Security &amp; 2FA</span></router-link
        >
      </li>
      <li>
        <a @click="logout"
          ><vue-feather type="log-in"></vue-feather
          ><span style="text-transform: none">Log out</span></a
        >
      </li>
    </ul>

    <!-- Company Entity Switcher Modal -->
    <UserGroupSwitcher v-if="canSwitchUserGroups" ref="userGroupSwitcher" />
  </li>
</template>

<script>
import { signOut } from 'firebase/auth';
import { newAuth } from '@/firebase/firebaseInit';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import UserGroupSwitcher from '@/components/header/UserGroupSwitcher.vue';
import { useToast } from 'vue-toastification';
import { userGroupService } from '@/services/userGroupService';

const sorted = (list) => (Array.isArray(list) ? [...list].sort() : []);

// Everything in userAddData that decides what the user may see: route guards,
// menu entries and the company entity switcher are all derived from these fields.
function accessFingerprint(userData) {
  return JSON.stringify({
    userGroup: userData?.userGroup ?? null,
    currentUserGroupId: userData?.currentUserGroupId ?? null,
    roles: sorted(userData?.roles),
    partnerTypes: sorted(userData?.partnerTypes),
    availableUserGroups: userData?.availableUserGroups ?? null,
    partnerSettings: userData?.partnerSettings ?? null,
  });
}

export default {
  name: 'Profile',
  components: {
    UserGroupSwitcher,
  },
  setup() {
    return { toast: useToast() };
  },
  props: {
    invertColor: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      email: '',
      firstName: '',
      lastName: '',
      isApiPartner: false,
      canSwitchUserGroups: false,
      availableUserGroups: null,
      refreshingPermissions: false,
    };
  },
  computed: {
    initials() {
      const first = this.firstName?.trim().charAt(0) || '';
      const last = this.lastName?.trim().charAt(0) || '';
      const combined = (first + last).toUpperCase();
      return combined || '?';
    },
  },
  created() {
    // When the component is created, get the user's data from local storage
    const userData = getUserAddDataFromLocalStorage();

    if (userData) {
      if (userData.firstName) this.firstName = userData.firstName;
      if (userData.lastName) this.lastName = userData.lastName;
      if (userData.email) this.email = userData.email;
    }

    if (userData && userData.partnerTypes) {
      this.isApiPartner = userData.partnerTypes.includes('API_PARTNER');
    }

    // Keep visibility aligned with backend authorization: the explicit role
    // OR two configured company contexts is enough to open the switcher.
    this.canSwitchUserGroups = Boolean(userData?.canSwitchUserGroups);
    this.availableUserGroups = userData?.availableUserGroups || null;
  },
  methods: {
    logout: async function () {
      localStorage.removeItem('User');
      localStorage.removeItem('userAddData');
      await signOut(newAuth).catch((error) => console.error('Error signing out:', error));
      // Full page load, so nothing this session cached stays behind for the next one.
      window.location.assign('/auth/login');
    },
    // Roles are cached in localStorage at login; this re-reads them on demand so a
    // change made by an admin in the meantime applies without logging out and in again.
    async refreshPermissions() {
      if (this.refreshingPermissions) {
        return;
      }
      this.refreshingPermissions = true;

      try {
        const { userAddData } = getUserAddDataFromLocalStorage();
        const freshUserData = await userGroupService.fetchCurrentUserData();
        const updatedUserData = { ...userAddData, ...freshUserData };

        if (accessFingerprint(userAddData) === accessFingerprint(updatedUserData)) {
          this.toast.info('Your permissions are already up to date.');
          return;
        }

        userGroupService.updateLocalStorage(updatedUserData);
        this.toast.success('Your permissions were updated. Reloading...');

        // Same landing as a company switch: the current page may no longer be permitted.
        setTimeout(() => {
          window.location.assign('/');
        }, 500);
      } catch (error) {
        console.error('Error refreshing permissions:', error);
        this.toast.error('Failed to refresh permissions. Please try again.');
      } finally {
        this.refreshingPermissions = false;
      }
    },
    openUserGroupSwitcher() {
      if (this.$refs.userGroupSwitcher) {
        this.$refs.userGroupSwitcher.open();
      }
    },
  },
};
</script>

<style scoped>
.text-inverted,
.text-inverted span,
.text-inverted p,
.text-inverted i {
  color: #fff !important;
}

/* Initials avatar: hidden on large screens, shown when media-body is hidden (≤810px) */
.profile-icon--always-visible {
  display: none;
}

.profile-avatar-sm {
  width: 28px;
  height: 28px;
  font-size: 11px;
}

.profile-nav:hover .profile-trigger-hover-bridge::after {
  content: '';
  position: absolute;
  left: -20px;
  right: -20px;
  bottom: -30px;
  height: 30px;
}

:deep(.profile-dropdown) {
  min-width: 220px !important;
}

/* Prevent header from inheriting menu item hover */
.profile-dropdown-header {
  cursor: default;
}

.profile-dropdown-header:hover {
  background-color: transparent !important;
}
</style>

<style>
/* Circle mode (≤810px): show initials avatar + name in dropdown */
@media only screen and (max-width: 810px) {
  .page-header .header-wrapper .nav-right .profile-nav .profile-icon--always-visible {
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 4px;
  }
  .profile-dropdown-header__name {
    display: block !important;
  }
  .profile-dropdown-header__email {
    margin-top: 0.25rem !important;
  }
}

/* Full version: hide name, email always visible */
@media only screen and (min-width: 811px) {
  .profile-dropdown-header__name {
    display: none !important;
  }
  .profile-dropdown-header__email {
    margin-top: 0 !important;
  }
}
</style>
